import { useMemo, useCallback } from 'react';
import { Extension } from '@codemirror/state';
import { keymap, EditorView } from '@codemirror/view';

export type Reference = {
  path: string;
  line: number;
  column: number;
  preview: string;
  matchStart: number;
  matchEnd: number;
};

// Get the word at cursor position
function getWordAtPosition(view: EditorView): { word: string; from: number; to: number } | null {
  const { state } = view;
  const pos = state.selection.main.head;
  const line = state.doc.lineAt(pos);
  const lineText = line.text;
  const linePos = pos - line.from;

  // Find word boundaries
  let start = linePos;
  let end = linePos;

  // Move backward to find start of word
  while (start > 0 && /[\w$]/.test(lineText[start - 1])) {
    start--;
  }

  // Move forward to find end of word
  while (end < lineText.length && /[\w$]/.test(lineText[end])) {
    end++;
  }

  if (start === end) return null;

  const word = lineText.slice(start, end);
  return { word, from: line.from + start, to: line.from + end };
}

// Find all references to a symbol in given code
export function findReferencesInCode(code: string, symbolName: string): Array<{ line: number; column: number; preview: string; matchStart: number; matchEnd: number }> {
  const references: Array<{ line: number; column: number; preview: string; matchStart: number; matchEnd: number }> = [];
  const lines = code.split('\n');

  // Create a regex that matches the symbol as a whole word
  const symbolRegex = new RegExp(`\\b${symbolName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'g');

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    let match;

    while ((match = symbolRegex.exec(line)) !== null) {
      references.push({
        line: i + 1,
        column: match.index + 1,
        preview: line.trim(),
        matchStart: match.index - (line.length - line.trim().length - (line.length - line.trimEnd().length) / 2),
        matchEnd: match.index + symbolName.length,
      });
    }
  }

  return references;
}

type UseFindReferencesParams = {
  workingFiles: Record<string, string>;
  onShowReferences: (symbolName: string, references: Reference[]) => void;
};

export function useFindReferences({
  workingFiles,
  onShowReferences,
}: UseFindReferencesParams) {
  // Find all references to a symbol across all files
  const findAllReferences = useCallback((symbolName: string): Reference[] => {
    const allReferences: Reference[] = [];

    for (const [path, code] of Object.entries(workingFiles)) {
      const fileRefs = findReferencesInCode(code, symbolName);
      for (const ref of fileRefs) {
        allReferences.push({
          path,
          ...ref,
        });
      }
    }

    // Sort by path, then by line
    allReferences.sort((a, b) => {
      const pathCompare = a.path.localeCompare(b.path);
      if (pathCompare !== 0) return pathCompare;
      return a.line - b.line;
    });

    return allReferences;
  }, [workingFiles]);

  // Handler for Shift+F12
  const handleFindReferences = useCallback((view: EditorView): boolean => {
    const wordInfo = getWordAtPosition(view);
    if (!wordInfo) return false;

    const { word } = wordInfo;
    const references = findAllReferences(word);

    if (references.length > 0) {
      onShowReferences(word, references);
      return true;
    }

    return false;
  }, [findAllReferences, onShowReferences]);

  // Create extension with Shift+F12 binding
  const findReferencesExtension = useMemo<Extension>(() => [
    keymap.of([
      {
        key: 'Shift-F12',
        run: (view) => handleFindReferences(view),
      },
    ]),
  ], [handleFindReferences]);

  return { findReferencesExtension, findAllReferences };
}
